/** Variáveis disponíveis para interpolação nos campos do fluxo (sintaxe tipo mustache). */

/** Alinhado a `ConversationWorkflows::WorkflowVariableBindings` (`upstream`). */
export const WORKFLOW_FLOW_VARIABLE_UPSTREAM_GROUP = {
  id: 'upstream',
  label: 'Nó anterior',
  hint: 'As saídas são definidas pelo node conectado antes deste passo e recebem valor em tempo de execução.',
  variables: [],
};

const WORKFLOW_NODE_OUTPUT_VARIABLES = {
  http_request: [
    {
      label: 'Corpo da resposta HTTP',
      token: '{{upstream.output.body}}',
      sourceKind: 'http_request',
    },
    {
      label: 'Código de estado HTTP',
      token: '{{upstream.output.status_code}}',
      sourceKind: 'http_request',
    },
    {
      label: 'Cabeçalhos HTTP (JSON)',
      token: '{{upstream.output.headers_json}}',
      sourceKind: 'http_request',
    },
  ],
  ai_agent: [
    {
      label: 'Texto da resposta da IA',
      token: '{{upstream.output.message}}',
      sourceKind: 'ai_agent',
    },
    {
      label: 'Tokens usados no prompt',
      token: '{{upstream.output.usage.prompt_tokens}}',
      sourceKind: 'ai_agent',
    },
    {
      label: 'Tokens usados na conclusão',
      token: '{{upstream.output.usage.completion_tokens}}',
      sourceKind: 'ai_agent',
    },
    {
      label: 'Total de tokens da IA',
      token: '{{upstream.output.usage.total_tokens}}',
      sourceKind: 'ai_agent',
    },
  ],
  condition: [
    {
      label: 'Ramo escolhido',
      token: '{{upstream.output.branch_handle}}',
      sourceKind: 'condition',
    },
  ],
  wait: [
    {
      label: 'Próximo node agendado',
      token: '{{upstream.output.next_node_id}}',
      sourceKind: 'wait',
    },
    {
      label: 'Tempo de espera',
      token: '{{upstream.output.wait_value}}',
      sourceKind: 'wait',
    },
    {
      label: 'Unidade do tempo de espera',
      token: '{{upstream.output.wait_unit}}',
      sourceKind: 'wait',
    },
    {
      label: 'Espera agendada?',
      token: '{{upstream.output.scheduled}}',
      sourceKind: 'wait',
    },
  ],
  stop_flow: [
    {
      label: 'Fluxo interrompido?',
      token: '{{upstream.output.stopped}}',
      sourceKind: 'stop_flow',
    },
  ],
  default: [
    {
      label: 'Node executado com sucesso?',
      token: '{{upstream.output.ok}}',
      sourceKind: 'default',
    },
  ],
};

export function workflowOutputVariablesForNode(node) {
  if (!node || node.type !== 'action') return [];

  const kind = String(node.data?.kind || '');
  return (
    WORKFLOW_NODE_OUTPUT_VARIABLES[kind] ||
    WORKFLOW_NODE_OUTPUT_VARIABLES.default
  ).map(variable => ({ ...variable }));
}

const WORKFLOW_FLOW_VARIABLE_OTHER_GROUPS = [
  {
    id: 'conversation',
    label: 'Conversa',
    hint: 'Dados da conversa que está sendo processada pelo fluxo.',
    variables: [
      { label: 'ID interno da conversa', token: '{{conversation.id}}' },
      { label: 'Display ID da conversa', token: '{{conversation.display_id}}' },
      { label: 'UUID da conversa', token: '{{conversation.uuid}}' },
      { label: 'Status da conversa', token: '{{conversation.status}}' },
      { label: 'Prioridade da conversa', token: '{{conversation.priority}}' },
      { label: 'ID da inbox da conversa', token: '{{conversation.inbox_id}}' },
      { label: 'ID do contato', token: '{{conversation.contact_id}}' },
      {
        label: 'ID do agente atribuído',
        token: '{{conversation.assignee_id}}',
      },
      { label: 'ID do time', token: '{{conversation.team_id}}' },
      {
        label: 'Atributos personalizados da conversa (JSON)',
        token: '{{conversation.custom_attributes_json}}',
      },
    ],
  },
  {
    id: 'contact',
    label: 'Contacto',
    hint: 'Dados do contato associado à conversa atual.',
    variables: [
      { label: 'ID do contato', token: '{{contact.id}}' },
      { label: 'Nome do contato', token: '{{contact.name}}' },
      { label: 'Email do contato', token: '{{contact.email}}' },
      { label: 'Telefone do contato', token: '{{contact.phone_number}}' },
      { label: 'Identificador do contato', token: '{{contact.identifier}}' },
      {
        label: 'Atributos personalizados do contato (JSON)',
        token: '{{contact.custom_attributes_json}}',
      },
    ],
  },
  {
    id: 'message',
    label: 'Mensagem',
    hint: 'Dados da mensagem que disparou o fluxo, quando esse contexto estiver disponível.',
    variables: [
      { label: 'ID da mensagem', token: '{{message.id}}' },
      { label: 'Conteúdo da mensagem', token: '{{message.content}}' },
      { label: 'Tipo da mensagem', token: '{{message.message_type}}' },
      { label: 'ID da inbox da mensagem', token: '{{message.inbox_id}}' },
      {
        label: 'ID da conversa da mensagem',
        token: '{{message.conversation_id}}',
      },
      { label: 'Mensagem privada?', token: '{{message.private}}' },
    ],
  },
  {
    id: 'workflow',
    label: 'Fluxo',
    variables: [
      { label: 'ID do fluxo', token: '{{workflow.id}}' },
      { label: 'Nome do fluxo', token: '{{workflow.name}}' },
    ],
  },
  {
    id: 'account',
    label: 'Conta',
    variables: [
      { label: 'ID da conta', token: '{{account.id}}' },
      { label: 'Nome da conta', token: '{{account.name}}' },
    ],
  },
  {
    id: 'kanban_item',
    label: 'Cartão Kanban',
    hint: 'Dados do cartão Kanban ligado à conversa ou ao evento que disparou o fluxo.',
    variables: [
      { label: 'ID do cartão Kanban', token: '{{kanban_item.id}}' },
      { label: 'ID do funil', token: '{{kanban_item.funnel_id}}' },
      { label: 'Etapa atual (chave)', token: '{{kanban_item.funnel_stage}}' },
      { label: 'Título do cartão', token: '{{kanban_item.title}}' },
      {
        label: 'Status do cartão (won/lost/open)',
        token: '{{kanban_item.status}}',
      },
      { label: 'Prioridade do cartão', token: '{{kanban_item.priority}}' },
      { label: 'Valor do cartão', token: '{{kanban_item.value}}' },
      {
        label: 'Display ID da conversa ligada',
        token: '{{kanban_item.conversation_display_id}}',
      },
    ],
  },
];

/** «Nó anterior» em primeiro lugar (estilo n8n). */
export const WORKFLOW_FLOW_VARIABLE_GROUPS = [
  WORKFLOW_FLOW_VARIABLE_UPSTREAM_GROUP,
  ...WORKFLOW_FLOW_VARIABLE_OTHER_GROUPS,
];

export const WORKFLOW_VARIABLE_DRAG_TYPE =
  'application/x-chatwoot-workflow-var';
